import { useState, useEffect, useCallback } from "react";
import { CreditCard, CheckCircle, Spinner, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * PayNowButton — multi-tenant checkout entry point on QuoteDetailPage.
 *
 * The backend `/payments/v1/checkout/session` endpoint detects the tenant
 * (US Air Compressor / Air Compressor Canada) from the quote heading and
 * routes to either Stripe Checkout or Elavon Converge Hosted Payments.
 * The button is processor-agnostic — it just follows whichever URL the
 * server returns.
 *
 * Lifecycle:
 *  1. Render → call /payments/v1/checkout/session → redirect to processor URL.
 *  2. After payment, customer returns to /quote/:id?paid=success&session_id=...
 *     - Stripe substitutes {CHECKOUT_SESSION_ID} for us.
 *     - Elavon's webhook 303-redirects back with the token already substituted.
 *  3. Component detects session_id in URL and polls /checkout/status/:id every
 *     2s up to 6 attempts. Once `payment_status === "paid"` we show a green
 *     success card.
 *  4. The QuotePaymentChip in the header (which fetches /payments) flips to PAID
 *     because the journal entry was written by the webhook (or status path).
 */
const PROCESSOR_LABEL = {
  stripe: "Stripe",
  elavon: "Converge",
};
const PROCESSOR_TAGLINE = {
  stripe: "Secure checkout via Stripe. Cards, Apple Pay, Google Pay accepted.",
  elavon: "Secure checkout via Elavon Converge. All major credit cards accepted.",
};

export default function PayNowButton({ headingId, magicToken = "", balance = 0, isInvoice = false }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [processor, setProcessor] = useState("stripe"); // updated after first session creation

  // Detect return on mount
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [returned, setReturned] = useState(null); // "success" | "cancel" | null

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const sid = params.get("session_id");
    if (paid === "success" && sid) {
      setReturned("success");
      pollStatus(sid, 0);
    } else if (paid === "cancel") {
      setReturned("cancel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = useCallback(async (sid, attempt) => {
    if (attempt > 6) return;
    setPolling(true);
    try {
      const r = await api.get(`/payments/v1/checkout/status/${encodeURIComponent(sid)}`);
      setPollResult(r.data);
      if (r.data?.processor) setProcessor(r.data.processor);
      if (r.data?.payment_status !== "paid" && attempt < 6) {
        setTimeout(() => pollStatus(sid, attempt + 1), 2000);
      } else {
        setPolling(false);
        if (r.data?.payment_status === "paid") {
          // Strip query params now that we have confirmation
          const url = new URL(window.location.href);
          url.searchParams.delete("paid");
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
        }
      }
    } catch {
      setPolling(false);
    }
  }, []);

  const startCheckout = async () => {
    setCreating(true); setError("");
    try {
      const r = await api.post("/payments/v1/checkout/session", {
        heading_id: headingId,
        magic_token: magicToken || undefined,
      });
      if (r.data?.processor) setProcessor(r.data.processor);
      // Redirect the browser to the processor-hosted checkout page.
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not start checkout");
      setCreating(false);
    }
  };

  // Hide for non-invoiced quotes or zero balance
  if (!isInvoice || !balance || balance <= 0.005) return null;

  const procLabel = PROCESSOR_LABEL[processor] || "secure processor";
  const procTagline = PROCESSOR_TAGLINE[processor] ||
    "Secure checkout. All major credit cards accepted.";

  // Successful return state
  if (returned === "success" && pollResult?.payment_status === "paid") {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 mb-3 flex items-start gap-2" data-testid="pay-success-banner">
        <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-bold text-emerald-800">Payment received — thank you!</div>
          <div className="text-[11px] text-emerald-700 mt-0.5">
            ${(pollResult.amount_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} {(pollResult.currency || "usd").toUpperCase()}
            {pollResult.journal_entry_id && <> · Journal entry #{pollResult.journal_entry_id}</>}
            <> · {procLabel} session {pollResult.session_id?.slice(-8)}</>
          </div>
        </div>
      </div>
    );
  }

  // Pending / polling state after redirect back from processor
  if (returned === "success" && polling) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 mb-3 flex items-center gap-2" data-testid="pay-pending-banner">
        <Spinner size={16} className="animate-spin text-blue-600" />
        <span className="text-sm text-blue-800">Confirming payment with {procLabel}…</span>
      </div>
    );
  }

  if (returned === "cancel") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-3 flex items-start gap-2" data-testid="pay-cancel-banner">
        <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-sm font-bold text-amber-800">Payment cancelled.</div>
          <button onClick={startCheckout} disabled={creating}
            data-testid="pay-now-retry-btn"
            className="mt-1.5 inline-flex items-center gap-1.5 h-8 px-3 bg-blue-700 hover:bg-blue-800 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm">
            <CreditCard size={11} weight="fill" /> Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3" data-testid="pay-now-cta">
      <button onClick={startCheckout} disabled={creating}
        data-testid="pay-now-btn"
        className="inline-flex items-center gap-2 h-10 px-5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold uppercase tracking-wider rounded-sm disabled:opacity-60 shadow-sm">
        {creating ? <Spinner size={14} className="animate-spin" /> : <CreditCard size={16} weight="fill" />}
        {creating ? `Redirecting to ${procLabel}…` : `Pay $${balance.toLocaleString("en-US", { minimumFractionDigits: 2 })} now`}
      </button>
      {error && <div className="text-xs text-rose-600 mt-1.5" data-testid="pay-now-error">{error}</div>}
      <div className="text-[10px] text-slate-400 mt-1" data-testid="pay-now-tagline">
        {procTagline}
      </div>
    </div>
  );
}
